import React, { useState, useCallback, useMemo } from 'react';
import styles from './StructureTree.module.scss';

// Иконки
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const FolderIcon = () => (
  <svg className={styles.nodeIcon} viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
  </svg>
);

const ExpandAllIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="7 13 12 18 17 13" />
    <polyline points="7 6 12 11 17 6" />
  </svg>
);

const CollapseAllIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 11 12 6 7 11" />
    <polyline points="17 18 12 13 7 18" />
  </svg>
);

const EmptyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <line x1="9" y1="14" x2="15" y2="14" />
  </svg>
);

const Spinner = ({ size = 16 }) => (
  <svg
    className={styles.spinner}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

// Компонент для отрисовки соединительных линий дерева
const INDENT_SIZE = 28;

const TreeLines = ({ level, connectorLevels, isLastChild }) => {
  if (level === 0) return null;

  const lines = [];

  // Вертикальные линии для продолжающихся уровней
  connectorLevels.forEach(connectorLevel => {
    lines.push(
      <span
        key={`v-${connectorLevel}`}
        className={styles.treeLine}
        style={{ left: `${connectorLevel * INDENT_SIZE + 14}px` }}
      />
    );
  });

  // Горизонтальная линия к текущему элементу + вертикальная часть
  const currentLeft = (level - 1) * INDENT_SIZE + 14;
  lines.push(
    <span
      key="current"
      className={`${styles.treeLine} ${isLastChild ? styles.lastChild : ''}`}
      style={{ left: `${currentLeft}px` }}
    />,
    <span
      key="horizontal"
      className={styles.treeLineHorizontal}
      style={{ left: `${currentLeft}px` }}
    />
  );

  return <div className={styles.treeLines}>{lines}</div>;
};

const collectParentIds = (nodes, parentIds = new Set()) => {
  nodes.forEach(node => {
    if (node.children && node.children.length > 0) {
      parentIds.add(node.id || node.name);
      collectParentIds(node.children, parentIds);
    }
  });
  return parentIds;
};

const flattenTree = (nodes, expandedIds, level = 0, result = [], rowIndex = { current: 0 }, connectorLevels = []) => {
  const totalNodes = nodes.length;

  nodes.forEach((node, idx) => {
    const nodeId = node.id || node.name;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(nodeId);
    // const childCount = hasChildren ? node.children.length : 0;
    const isLastChild = idx === totalNodes - 1;

    // Копируем массив уровней с вертикальными линиями
    const currentConnectors = [...connectorLevels];

    result.push({
      ...node,
      nodeId,
      level,
      hasChildren,
      isExpanded,
      // childCount,
      isLastChild,
      connectorLevels: currentConnectors,
      rowIndex: rowIndex.current++
    });

    if (hasChildren && isExpanded) {
      // Добавляем текущий уровень в список, если это НЕ последний элемент
      const nextConnectors = [...currentConnectors];
      if (!isLastChild) {
        nextConnectors.push(level);
      }
      flattenTree(node.children, expandedIds, level + 1, result, rowIndex, nextConnectors);
    }
  });

  return result;
};

const StructureTree = ({ structure, isLoading: externalLoading = false }) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [processingAction, setProcessingAction] = useState(null); // 'expand' | 'collapse' | null
  const [loadingNodeId, setLoadingNodeId] = useState(null);

  const isLoading = externalLoading || !!processingAction;

  const unwrappedStructure = useMemo(() => {
    return structure.flatMap(node => node.children || []);
  }, [structure]);

  const allParentIds = useMemo(() => {
    return collectParentIds(unwrappedStructure);
  }, [unwrappedStructure]);

  const flatNodes = useMemo(() => {
    return flattenTree(unwrappedStructure, expandedIds);
  }, [unwrappedStructure, expandedIds]);

  const toggleNode = useCallback((nodeId) => {
    setLoadingNodeId(nodeId);
    // Небольшая задержка для визуального эффекта загрузки
    setTimeout(() => {
      setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return next;
      });
      setLoadingNodeId(null);
    }, 150);
  }, []);

  const expandAll = useCallback(() => {
    setProcessingAction('expand');
    setTimeout(() => {
      setExpandedIds(new Set(allParentIds));
      setProcessingAction(null);
    }, 300);
  }, [allParentIds]);

  const collapseAll = useCallback(() => {
    setProcessingAction('collapse');
    setTimeout(() => {
      setExpandedIds(new Set());
      setProcessingAction(null);
    }, 300);
  }, []);

  const isAllExpanded = expandedIds.size === allParentIds.size && allParentIds.size > 0;
  const isAllCollapsed = expandedIds.size === 0;

  // Показываем лоадер при загрузке данных
  if (externalLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.loadingState}>
            <Spinner size={40} />
            <p>Загрузка данных...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!unwrappedStructure || unwrappedStructure.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.emptyState}>
            <EmptyIcon />
            <p>Нет данных для отображения</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <button
          className={styles.controlBtn}
          onClick={expandAll}
          disabled={isAllExpanded || isLoading}
          title="Раскрыть все ветки"
        >
          {processingAction === 'expand' ? <Spinner /> : <ExpandAllIcon />}
          Раскрыть всё
        </button>
        <button
          className={styles.controlBtn}
          onClick={collapseAll}
          disabled={isAllCollapsed || isLoading}
          title="Свернуть все ветки"
        >
          {processingAction === 'collapse' ? <Spinner /> : <CollapseAllIcon />}
          Свернуть всё
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr className={styles.headerRow}>
                <th>№</th>
                <th>Название</th>
              </tr>
            </thead>
            <tbody className={styles.tbody}>
              {flatNodes.map((node, index) => (
                <tr
                  key={node.nodeId + '_' + index}
                  className={styles.row}
                  style={{ animationDelay: `${Math.min(index * 0.02, 0.3)}s` }}
                >
                  <td className={styles.cell}>
                    {node.number ? (
                      <span className={styles.numberBadge}>{node.number}</span>
                    ) : null}
                  </td>
                  <td className={styles.cell}>
                    <div
                      className={styles.nameCell}
                      style={{ paddingLeft: `${node.level * INDENT_SIZE}px` }}
                    >
                      <TreeLines
                        level={node.level}
                        connectorLevels={node.connectorLevels}
                        isLastChild={node.isLastChild}
                      />
                      {node.hasChildren ? (
                        <>
                          <button
                            className={`${styles.expandBtn} ${node.isExpanded ? styles.expanded : ''} ${loadingNodeId === node.nodeId ? styles.loading : ''}`}
                            onClick={() => toggleNode(node.nodeId)}
                            aria-label={node.isExpanded ? 'Свернуть' : 'Раскрыть'}
                            aria-expanded={node.isExpanded}
                            disabled={loadingNodeId === node.nodeId}
                          >
                            {loadingNodeId === node.nodeId ? <Spinner size={14} /> : <ChevronIcon />}
                          </button>
                          <FolderIcon />
                        </>
                      ) : (
                        <span className={styles.leafMarker} />
                      )}
                      <span className={`${styles.nodeName} ${node.hasChildren ? styles.parent : styles.leaf}`}>
                        {node.name}
                      </span>
                      {/* {node.hasChildren && node.childCount > 0 && (
                        <span className={styles.childCount}>{node.childCount}</span>
                      )} */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StructureTree;
